import React, { useEffect, useState, useCallback, Suspense } from 'react';
import { connect } from 'react-redux';
import {
  getVehicleGroups,
  changeVehicleGroupStatus,
} from 'store/requests/vehicle';
import {
  DownloadOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  FilterOutlined,
  MoreOutlined,
  PlusOutlined,
  CheckOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import 'antd/dist/antd.css';
import {
  Button,
  Col,
  ConfigProvider,
  Dropdown,
  Form,
  Modal,
  notification,
  Row,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import {
  debouncedDataLoadWaitMs,
  ORDER_TYPES,
  STATUS,
} from 'helpers/constants';
import trTR from 'antd/es/locale/tr_TR';
import { CSVLink } from 'react-csv';
import { useHistory } from 'react-router-dom';
import userHaveRolePermission from 'helpers/userHaveRolePermission';
import { PAGE_URLS } from 'helpers/endpoints';
const Add = React.lazy(() => import('./Add'));
const Edit = React.lazy(() => import('./Edit'));
const VehicleGroupsTableFilters = React.lazy(
  () => import('./VehicleGroupsTableFilters'),
);

let lastQuery = false;

const VehicleGroupView = ({ route }) => {
  const history = useHistory();
  const { isViewEnabled, isCrudEnabled } = userHaveRolePermission(route?.name);
  if (!isViewEnabled) {
    history.push(`${PAGE_URLS.AUTH}${PAGE_URLS.CONTROL}`);
  }
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { confirm } = Modal;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({});
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [tableParams, setTableParams] = useState({
    pagination: {
      showSizeChanger: true,
      showQuickJumper: true,
      showTotal: (total) => `Toplam ${total} kayıt`,
      pageSizeOptions: [20, 50, 100],
      size: 'small',
      current: 1,
      pageSize: 20,
    },
  });

  const columns = [
    { dataIndex: 'id', title: t('id'), sorter: true, showSorterTooltip: false },
    {
      dataIndex: 'name',
      title: t('vehicleGroup'),
      sorter: true,
      showSorterTooltip: false,
    },
    {
      title: t('isActive'),
      dataIndex: 'active',
      sorter: true,
      showSorterTooltip: false,
      render: (active) =>
        active ? (
          <Tag color={'green'}>{t('active')}</Tag>
        ) : (
          <Tag color={'red'}>{t('passive')}</Tag>
        ),
    },
    {
      width: 50,
      render: (dataItem) =>
        isCrudEnabled && (
          <Dropdown
            menu={{
              items: [
                {
                  label: t('edit'),
                  icon: <EditOutlined />,
                  onClick: () => {
                    setIsEditModalOpen(true);
                  },
                },
                {
                  danger: dataItem.active,
                  label: dataItem.active ? t('delete') : t('activate'),
                  icon: dataItem.active ? <CloseOutlined /> : <CheckOutlined />,
                  onClick: () => {
                    showChangeVehicleGroupStatus({
                      vehicleGroupIds: [selectedItem.id],
                      status: !dataItem.active,
                    });
                  },
                },
              ],
            }}
            placement="bottomRight"
            arrow
            trigger={['click']}
            onClick={() => setSelectedItem(dataItem)}
          >
            <Button shape="circle" icon={<MoreOutlined />}></Button>
          </Dropdown>
        ),
    },
  ];

  function showChangeVehicleGroupStatus(data) {
    confirm({
      title: t('modalDeleteTitle'),
      icon: <ExclamationCircleOutlined />,
      content: !data.status ? t('vehicleGroupDelete') : t('vehicleGroupActive'),
      okText: !data.status ? t('delete') : t('activate'),
      okType: !data.status ? 'danger' : 'primary',
      cancelText: t('cancel'),
      centered: true,
      onOk() {
        changeVehicleGroupStatus(data).then((res) => {
          if (res.isSuccess) {
            notificationChangeStatus(STATUS.SUCCESS);
            lastQuery = null;
            const queryString = getQueryString();
            loadData(queryString);
          } else {
            notification.error({ message: res.message });
          }
        });
      },
      onCancel() {},
    });
  }

  const notificationChangeStatus = (type) => {
    notification[type]({
      message: t('vehicleGroupEdited'),
      description: t('vehicleGroupStatusChanged'),
    });
  };

  const getQueryString = () => {
    let queryString = '';
    if (Object.values(filters).some((item) => !!item)) {
      Object.keys(filters).forEach((item, idx) => {
        queryString += `filter=${item}=='${filters[item]}'`;
      });
    }
    queryString = `${queryString}&page=${tableParams.pagination.current - 1}&size=${tableParams.pagination.pageSize}`;
    if (tableParams.column) {
      queryString = `${queryString}&sort=${tableParams.column.dataIndex},${ORDER_TYPES[tableParams.order]}`;
    }
    return queryString;
  };

  async function loadDataWithDebounce(queryString) {
    if (!loading) {
      lastQuery = queryString;
      setLoading(true);
      getVehicleGroups(queryString)
        .then((res) => {
          const { _embedded, page } = res;
          const { vehicleGroupResponseDtoList } = _embedded ? _embedded : [];
          setRows(
            vehicleGroupResponseDtoList ? vehicleGroupResponseDtoList : [],
          );
          setTableParams({
            ...tableParams,
            pagination: {
              ...tableParams.pagination,
              total: page.totalElements,
            },
          });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }

  const loadData = useCallback(
    debounce((queryString) => {
      loadDataWithDebounce(queryString);
    }, debouncedDataLoadWaitMs),
  );

  useEffect(() => {
    fetchData();
    setIsFiltersOpen(false);
    return () => {
      lastQuery = null;
      setLoading(false);
    };
  }, [JSON.stringify(tableParams), JSON.stringify(filters)]);

  const fetchData = () => {
    const queryString = getQueryString();
    loadData(queryString);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      ...sorter,
    });
  };

  const actions = [
    {
      isCrudEnabled,
      type: 'primary',
      title: t('add'),
      icon: <PlusOutlined />,
      onClick: () => setIsAddModalOpen(true),
      disabled: false,
    },
    {
      isCrudEnabled: true,
      type: '',
      title: t('download'),
      customContent: (
        <CSVLink
          filename={t('vehicleGroupsTitle')}
          data={[
            columns.map((item) => item.title),
            ...rows.map((item) => [item.id, item.name]),
          ]}
        >
          <Button
            style={{ marginRight: '3px' }}
            shape="round"
            icon={<DownloadOutlined />}
          ></Button>
        </CSVLink>
      ),
      onClick: () => {},
      disabled: false,
    },
  ];

  const renderActionButtons = () => {
    return actions.map((item, idx) => {
      return (
        <Tooltip title={item.title} key={idx}>
          {item.isCrudEnabled &&
            (item.customContent ?? (
              <Button
                type={item?.type}
                shape="round"
                icon={item?.icon}
                onClick={item.onClick}
                disabled={item.disabled}
                style={{ marginBottom: '10px', marginRight: '3px' }}
                danger={item?.danger}
              />
            ))}
        </Tooltip>
      );
    });
  };
  const renderSelectedFilters = () => {
    return Object.keys(filters).map((item, idx) => {
      if (filters[item]) {
        return (
          <Tag
            key={idx}
            closable
            color="geekblue"
            onClose={(e) => {
              e.preventDefault();
              removeItemFromFilters(item);
            }}
          >
            {`${item === 'name' ? t('vehicleGroup') : t(item)} : ${t(`${filters[item]}`)}`}
          </Tag>
        );
      }
    });
  };

  const removeItemFromFilters = (destinationItem) => {
    Object.keys(filters).forEach((item) => {
      if (item === destinationItem) {
        const editedFilters = {
          ...filters,
          [destinationItem]: undefined,
        };
        setFilters(editedFilters);
        form.setFieldsValue(editedFilters);
      }
    });
  };

  return (
    <div>
      <Row
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'start',
        }}
      >
        <Col>{renderActionButtons()}</Col>
        <Col>
          <Button
            shape="round"
            icon={<FilterOutlined />}
            onClick={() => setIsFiltersOpen(!isFiltersOpen)}
          >
            {t('filters')}
          </Button>
        </Col>
      </Row>
      <Row style={{ display: 'flex', justifyContent: 'flex-end' }}>
        {renderSelectedFilters()}
      </Row>
      <Row style={{ marginTop: '10px' }}>
        <ConfigProvider locale={trTR}>
          <Table
            style={{ width: '100%' }}
            size="small"
            columns={columns}
            dataSource={rows}
            pagination={tableParams.pagination}
            loading={loading}
            onChange={handleTableChange}
            rowKey={(record) => record.id}
            scroll={{
              y: '70vh',
            }}
          />
        </ConfigProvider>
      </Row>
      <Suspense fallback={() => {}}>
        {isFiltersOpen && (
          <VehicleGroupsTableFilters
            setIsFiltersOpen={setIsFiltersOpen}
            setFilters={setFilters}
            form={form}
          />
        )}
        {isAddModalOpen && (
          <Add
            onCloseHandler={() => setIsAddModalOpen(false)}
            onSuccessCallback={fetchData}
          />
        )}
        {isEditModalOpen && (
          <Edit
            vehicleGroupId={selectedItem.id}
            onCloseHandler={() => setIsEditModalOpen(false)}
            onSuccessCallback={fetchData}
          />
        )}
      </Suspense>
    </div>
  );
};

export default VehicleGroupView;
